import {FC, useEffect, useMemo, useState} from 'react'
import {api, Booking, BookingPayment, BookingStatus, PaymentMethodValue, ApiError} from '../../services/api'
import {Pagination} from '../../components/Pagination'
import {Spinner} from '../../components/Spinner'
import {useToast} from '../../components/Toast'
import {confirmDialog, promptDialog} from '../../components/dialog'
import {PageHead} from '../../components/PageHead'
import {ListToolbar} from '../../components/ListToolbar'
import {ModalPortal} from '../../components/ModalPortal'
import {BookingTimelineModal} from '../../components/BookingTimelineModal'
import {useIsMobile} from '../../hooks/useIsMobile'

type ActKind = 'approve' | 'reject' | 'waitlist' | 'cancel' | 'checkin' | 'checkout' | 'refund' | 'resend'

const ACCENT = '#039540'
const INK = '#1b1b29'
const MUTED = '#7e8299'
const PAGE_SIZE = 10

const TABS: {label: string; value: BookingStatus | 'ALL'}[] = [
  {label: 'Pending', value: 'PENDING'},
  {label: 'Waitlisted', value: 'WAITLISTED'},
  {label: 'Approved', value: 'APPROVED'},
  {label: 'Completed', value: 'COMPLETED'},
  {label: 'Rejected', value: 'REJECTED'},
  {label: 'All', value: 'ALL'},
]

const STATUS_TONE: Record<BookingStatus, {color: string; bg: string}> = {
  PENDING: {color: '#b8770a', bg: 'rgba(255,168,0,0.15)'},
  WAITLISTED: {color: '#7c4dff', bg: 'rgba(124,77,255,0.12)'},
  APPROVED: {color: '#1f9d5b', bg: 'rgba(3,149,64,0.12)'},
  COMPLETED: {color: '#2b4eff', bg: 'rgba(43,78,255,0.12)'},
  REJECTED: {color: '#d9214e', bg: 'rgba(217,33,78,0.1)'},
  CANCELLED: {color: '#7e8299', bg: 'rgba(126,130,153,0.15)'},
}

const StatusPill: FC<{status: BookingStatus}> = ({status}) => {
  const t = STATUS_TONE[status]
  return <span style={{fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', borderRadius: 999, padding: '4px 10px', color: t.color, background: t.bg}}>{status}</span>
}

/**
 * Whether enough rent is on file to check in / proceed. Fixed-term needs the
 * full total (b.paid); open-ended monthly needs the first month recorded (rent
 * paidAmount > 0) or an active subscription.
 */
const isRentCovered = (b: Booking): boolean =>
  b.amount != null
    ? b.paid
    : Number(b.paidAmount ?? 0) > 0 || b.subscriptionStatus === 'active' || b.hasSubscription

/** Monthly-rent subscription state for a booking (open-ended stays). */
const RentBadge: FC<{b: Booking}> = ({b}) => {
  const pill = (label: string, color: string, bg: string) => (
    <span style={{fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', borderRadius: 999, padding: '4px 10px', color, background: bg, whiteSpace: 'nowrap'}}>{label}</span>
  )
  if (b.moveOutDate) return pill(`Move-out ${b.moveOutDate}`, '#b8770a', 'rgba(255,168,0,0.15)')
  const s = b.subscriptionStatus
  if (s === 'past_due') return pill('Rent past due', '#d9214e', 'rgba(217,33,78,0.1)')
  if (s === 'canceled') return pill('Rent canceled', '#7e8299', 'rgba(126,130,153,0.15)')
  if (s === 'active' || b.hasSubscription) return pill('Rent active', '#1f9d5b', 'rgba(3,149,64,0.12)')
  // No Stripe subscription — but rent has been recorded → paying manually (Zelle/cash).
  if (Number(b.paidAmount ?? 0) > 0) return pill('Manual rent', '#1f9d5b', 'rgba(3,149,64,0.12)')
  if (b.status === 'APPROVED') return pill('No rent set up', '#b8770a', 'rgba(255,168,0,0.15)')
  return <span style={{color: MUTED}}>—</span>
}

/** Renter has confirmed permanent departure — prompts the admin to check them out. */
const DepartureFlag: FC = () => (
  <span style={{fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', borderRadius: 999, padding: '4px 10px', color: '#b8770a', background: 'rgba(255,168,0,0.15)', whiteSpace: 'nowrap'}}>
    🧳 Departure confirmed — check out
  </span>
)

const BookingsPage: FC = () => {
  const [tab, setTab] = useState<BookingStatus | 'ALL'>('PENDING')
  const [search, setSearch] = useState('')
  const [rows, setRows] = useState<Booking[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [busy, setBusy] = useState<{id: string; kind: ActKind} | null>(null)
  const [payFor, setPayFor] = useState<Booking | null>(null)
  const [refundFor, setRefundFor] = useState<{booking: Booking; deposit: BookingPayment} | null>(null)
  const [timelineFor, setTimelineFor] = useState<Booking | null>(null)
  const [timelineBusyId, setTimelineBusyId] = useState<string | null>(null)

  // Always pull the latest booking (status + payments) when opening the timeline.
  const openTimeline = async (b: Booking) => {
    setTimelineBusyId(b.id)
    try {
      setTimelineFor(await api.getBooking(b.id))
    } catch {
      setTimelineFor(b) // fall back to the row data if the refetch fails
    } finally {
      setTimelineBusyId(null)
    }
  }
  const toast = useToast()
  const isMobile = useIsMobile()

  const load = async () => {
    setLoading(true)
    setError(null)
    try {
      setRows(await api.listBookings(tab === 'ALL' ? undefined : tab))
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to load bookings')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
    setPage(1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab])

  useEffect(() => {
    setPage(1)
  }, [search])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return rows
    return rows.filter((b) => {
      const haystack = [b.applicant?.firstName, b.applicant?.lastName, b.applicant?.email, b.bed?.code]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      return haystack.includes(q)
    })
  }, [rows, search])

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  useEffect(() => {
    if (page > pageCount) setPage(pageCount)
  }, [page, pageCount])
  const paged = useMemo(() => filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), [filtered, page])

  const replaceOrDrop = (updated: Booking, original: Booking) => {
    if (tab !== 'ALL' && updated.status !== tab) {
      setRows((list) => list.filter((x) => x.id !== original.id))
    } else {
      setRows((list) => list.map((x) => (x.id === original.id ? updated : x)))
    }
  }

  const act = async (b: Booking, kind: ActKind) => {
    let reason: string | undefined
    let depositReceived = false
    let transferFrom: string | undefined
    if (kind === 'reject') {
      const r = await promptDialog({title: 'Reject booking', inputLabel: 'Rejection reason (optional)', inputPlaceholder: 'Shown to the renter…', confirmText: 'Reject'})
      if (r === null) return
      reason = r || undefined
    }
    if (kind === 'cancel') {
      const ok = await confirmDialog({title: `Cancel booking for bed ${b.bed?.code ?? ''}?`, text: 'This ends the stay and frees the bed. The renter can book again.', confirmText: 'Cancel booking', danger: true})
      if (!ok) return
    }
    if (kind === 'approve') {
      const name = b.applicant?.firstName ?? 'this renter'
      const total = b.amount ? Number(b.amount) : 0
      if (Number(b.depositHeld) > 0) {
        // Deposit already paid on this booking (e.g. by card via Stripe) → don't
        // ask "did you receive it"; it's already on file. Just confirm approval.
        const dep = b.payments?.find((p) => p.status === 'PAID' && /deposit/i.test(p.periodLabel ?? ''))
        const via = dep?.method === 'CARD' ? ' by card' : dep ? ` via ${dep.method.toLowerCase()}` : ''
        const ok = await confirmDialog({
          title: `Approve ${name}'s booking?`,
          text: `Their $${Number(b.depositHeld).toFixed(2)} refundable deposit is already on file (paid${via}) — no new deposit is collected. Approve the booking for bed ${b.bed?.code ?? ''}?`,
          confirmText: 'Approve booking',
          cancelText: 'Cancel',
        })
        if (!ok) return
        depositReceived = true
      } else {
        const held = await api.transferableDeposit(b.id).catch(() => null)
        if (held) {
          const from = held.bedCode ? `bed ${held.bedCode}` : 'a previous booking'
          const ok = await confirmDialog({
            title: `${name} already has a $${Number(held.amount).toFixed(0)} deposit held`,
            text: `Their $${Number(held.amount).toFixed(2)} refundable deposit from ${from} (receipt ${held.receiptNumber}) was never refunded — it carries over to this booking, so no new deposit is collected.`,
            confirmText: 'Transfer deposit & approve',
            cancelText: 'Cancel',
          })
          if (!ok) return
          depositReceived = true
          transferFrom = held.bookingId
        } else {
          const ok = await confirmDialog({
            title: 'Did you receive the $100 deposit?',
            text: `Confirm you received the $100 refundable deposit from ${name}. It's held as security, separate from rent — rent owed stays${total ? ` $${total.toFixed(2)}` : ' the same'} and the $100 is refunded at clean departure.`,
            confirmText: 'Yes — $100 received, approve',
            cancelText: 'Cancel',
          })
          if (!ok) return
          depositReceived = true
        }
      }
    }
    if (kind === 'checkin') {
      const now = new Date().toLocaleString()
      const ok = await confirmDialog({
        title: `Check in ${b.applicant?.firstName ?? 'renter'} · Bed ${b.bed?.code ?? ''}?`,
        text: `This records the check-in now, at ${now}. Confirm the renter has arrived and taken the bed.`,
        confirmText: 'Confirm check-in',
        cancelText: 'Cancel',
      })
      if (!ok) return
    }
    if (kind === 'checkout') {
      const now = new Date().toLocaleString()
      const ok = await confirmDialog({
        title: `Check out ${b.applicant?.firstName ?? 'renter'} · Bed ${b.bed?.code ?? ''}?`,
        text: `This records the check-out now, at ${now}, and frees the bed. Confirm the renter has left and returned their personal key fob to its hook.`,
        confirmText: 'Confirm check-out',
        cancelText: 'Cancel',
        danger: true,
      })
      if (!ok) return
    }
    setBusy({id: b.id, kind})
    try {
      const updated =
        kind === 'approve' ? await api.approveBooking(b.id, depositReceived, transferFrom)
          : kind === 'waitlist' ? await api.waitlistBooking(b.id)
          : kind === 'cancel' ? await api.adminCancelBooking(b.id)
          : kind === 'checkin' ? await api.checkInBooking(b.id)
          : kind === 'checkout' ? await api.checkOutBooking(b.id)
          : await api.rejectBooking(b.id, reason)
      replaceOrDrop(updated, b)
      const msg = kind === 'checkin' ? 'Renter checked in.' : kind === 'checkout' ? 'Renter checked out — bed freed.' : `Booking ${updated.status.toLowerCase()}.`
      toast.success(msg)
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Action failed')
    } finally {
      setBusy(null)
    }
  }

  // Resend the welcome packet (directions, house guide, boarding pass) — for a
  // renter who lost it, gave a wrong address, or paid before it was automatic.
  const resendWelcome = async (b: Booking) => {
    const name = b.applicant?.firstName ?? 'this renter'
    const ok = await confirmDialog({
      title: `Resend the welcome packet to ${name}?`,
      text: `Sends the directions, house guide and bed boarding pass again to ${b.applicant?.email ?? 'their email'}. Safe to repeat — it doesn't change their booking or charge anything.`,
      confirmText: 'Send it again',
      cancelText: 'Cancel',
    })
    if (!ok) return
    setBusy({id: b.id, kind: 'resend'})
    try {
      const {to} = await api.resendWelcomePacket(b.id)
      toast.success(`Welcome packet sent to ${to}.`)
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Could not resend the welcome packet')
    } finally {
      setBusy(null)
    }
  }

  // Open the deposit-refund modal (itemized deductions + reasons → net refund).
  const refundDeposit = (b: Booking) => {
    const dep = b.payments.find((p) => p.status === 'PAID' && /deposit/i.test(p.periodLabel ?? ''))
    if (!dep) return toast.error('No held deposit to refund.')
    setRefundFor({booking: b, deposit: dep})
  }

  return (
    <div>
      <PageHead icon='📅' eyebrow='Operations' title='Bookings & Waitlist' sub='Approve requests, take payment, then check renters in and out.' />

      <ListToolbar search={search} onSearch={setSearch} placeholder='Search bookings…' />

      <div style={{display: 'flex', gap: isMobile ? 6 : 8, marginBottom: 18, flexWrap: 'wrap'}}>
        {TABS.map((t) => (
          <button key={t.value} onClick={() => setTab(t.value)} style={{fontSize: isMobile ? 13 : 14, fontWeight: 600, padding: isMobile ? '7px 12px' : '8px 16px', borderRadius: 999, cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap', border: '1px solid', borderColor: tab === t.value ? ACCENT : '#e5e7eb', background: tab === t.value ? ACCENT : '#fff', color: tab === t.value ? '#fff' : INK}}>
            {t.label}
          </button>
        ))}
      </div>

      <div className='card ccp-fade-up-2' style={{borderRadius: 16, border: 'none', boxShadow: '0 6px 18px rgba(27,27,41,0.06)'}}>
        <div className='card-body p-0'>
          {loading ? (
            <div style={{padding: 40, textAlign: 'center', color: MUTED}}>Loading…</div>
          ) : error ? (
            <div style={{padding: 40, textAlign: 'center', color: '#d9214e'}}>{error}</div>
          ) : filtered.length === 0 ? (
            <div style={{padding: 40, textAlign: 'center', color: MUTED}}>{search.trim() ? 'No bookings match your search.' : `No ${tab === 'ALL' ? '' : tab.toLowerCase()} bookings.`}</div>
          ) : (
            <>
              {isMobile ? (
                <div>
                  {paged.map((b) => {
                    const decidable = b.status === 'PENDING' || b.status === 'WAITLISTED'
                    const rowBusy = busy?.id === b.id
                    const spin = (k: ActKind, label: string) => (busy?.id === b.id && busy.kind === k ? <Spinner size={13} /> : label)
                    return (
                      <div key={b.id} style={{borderTop: '1px solid #f1f3f7', padding: '14px 16px'}}>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10}}>
                          <div style={{minWidth: 0}}>
                            <div style={{fontWeight: 600, color: INK}}>{b.applicant?.firstName} {b.applicant?.lastName}</div>
                            <div style={{color: MUTED, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{b.applicant?.email}</div>
                          </div>
                          <StatusPill status={b.status} />
                        </div>
                        <div style={{display: 'flex', flexWrap: 'wrap', gap: '4px 16px', marginTop: 8, fontSize: 13, color: MUTED, alignItems: 'center'}}>
                          <span style={{fontWeight: 700, color: INK}}>Bed {b.bed?.code ?? '—'}</span>
                          <span style={{fontWeight: 700, color: INK}}>{b.amount ? `$${b.amount}` : b.unitPrice ? `$${Number(b.unitPrice).toFixed(0)}/mo` : '—'}</span>
                          {b.amount != null ? (
                            <span style={{fontWeight: 700, ...(b.paid ? {color: '#1f9d5b'} : {color: '#b8770a'})}}>{b.paid ? 'Paid' : 'Unpaid'}</span>
                          ) : (
                            <RentBadge b={b} />
                          )}
                        </div>
                        <div style={{fontSize: 13, color: MUTED, marginTop: 4}}>
                          {b.startDate} → {b.endDate ?? b.moveOutDate ?? 'ongoing'}{b.months != null ? ` · ${b.months} mo` : ' · monthly'}{b.checkedInAt && !b.checkedOutAt ? ' · checked in' : ''}{b.checkedOutAt ? ' · checked out' : ''}
                        </div>
                        {b.departureConfirmedAt && !b.checkedOutAt && (
                          <div style={{marginTop: 6}}><DepartureFlag /></div>
                        )}
                        {(
                          <div style={{display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12}}>
                            <button onClick={() => openTimeline(b)} disabled={rowBusy || timelineBusyId === b.id} style={{...btnGhost, flex: 1}}>{timelineBusyId === b.id ? <Spinner size={13} /> : 'Timeline'}</button>
                            {decidable ? (
                              <>
                                {b.status === 'PENDING' && <button onClick={() => act(b, 'waitlist')} disabled={rowBusy} style={{...btnGhost, flex: 1}}>{spin('waitlist', 'Waitlist')}</button>}
                                <button onClick={() => act(b, 'reject')} disabled={rowBusy} style={{...btnDanger, flex: 1}}>{spin('reject', 'Reject')}</button>
                                <button onClick={() => act(b, 'approve')} disabled={rowBusy} style={{...btnPrimary, flex: 1}}>{spin('approve', 'Approve')}</button>
                              </>
                            ) : b.status === 'APPROVED' ? (
                              <>
                                {b.checkedInAt && !b.checkedOutAt ? (
                                  <button onClick={() => act(b, 'checkout')} disabled={rowBusy} style={{...btnPrimary, flex: 1}}>{spin('checkout', 'Check out')}</button>
                                ) : !b.checkedInAt && isRentCovered(b) ? (
                                  <button onClick={() => act(b, 'checkin')} disabled={rowBusy} style={{...btnPrimary, flex: 1}}>{spin('checkin', 'Check in')}</button>
                                ) : null}
                                {!b.checkedOutAt && (!isRentCovered(b) || b.amount == null) && (
                                  <button onClick={() => setPayFor(b)} disabled={rowBusy} style={{...btnGhost, flex: 1}}>Record payment</button>
                                )}
                                <button onClick={() => act(b, 'cancel')} disabled={rowBusy} style={{...btnDanger, flex: 1}}>{spin('cancel', 'Cancel')}</button>
                              </>
                            ) : null}
                            {b.status === 'APPROVED' && Number(b.depositHeld) > 0 && <button onClick={() => resendWelcome(b)} disabled={rowBusy} style={{...btnGhost, flex: 1}}>{spin('resend', 'Resend welcome')}</button>}
                            {Number(b.depositHeld) > 0 && <button onClick={() => refundDeposit(b)} disabled={rowBusy} style={{...btnGhost, flex: 1}}>{spin('refund', 'Refund deposit')}</button>}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              ) : (
              <div style={{overflowX: 'auto'}}>
                <table style={{width: '100%', borderCollapse: 'collapse', fontSize: 14}}>
                  <thead>
                    <tr style={{textAlign: 'left', color: MUTED, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em'}}>
                      <th style={th}>Renter</th>
                      <th style={th}>Bed</th>
                      <th style={th}>Stay</th>
                      <th style={th}>Rent</th>
                      <th style={{...th, minWidth: 130, whiteSpace: 'nowrap'}}>Rent status</th>
                      <th style={th}>Status</th>
                      <th style={{...th, textAlign: 'right'}}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paged.map((b) => {
                      const decidable = b.status === 'PENDING' || b.status === 'WAITLISTED'
                      const rowBusy = busy?.id === b.id
                      const spin = (k: ActKind, label: string) => (busy?.id === b.id && busy.kind === k ? <Spinner size={13} /> : label)
                      return (
                        <tr key={b.id} style={{borderTop: '1px solid #f1f3f7'}}>
                          <td style={td}>
                            <div style={{fontWeight: 600, color: INK}}>{b.applicant?.firstName} {b.applicant?.lastName}</div>
                            <div style={{color: MUTED, fontSize: 13}}>{b.applicant?.email}</div>
                          </td>
                          <td style={{...td, fontWeight: 700, color: INK}}>{b.bed?.code ?? '—'}</td>
                          <td style={{...td, color: MUTED, whiteSpace: 'nowrap'}}>
                            <div>{b.startDate} → {b.endDate ?? b.moveOutDate ?? 'ongoing'}</div>
                            <div style={{fontSize: 12}}>
                              {b.months != null ? `${b.months} month${b.months === 1 ? '' : 's'}` : 'monthly'}
                              {b.checkedInAt && !b.checkedOutAt ? ' · checked in' : ''}
                              {b.checkedOutAt ? ' · checked out' : ''}
                            </div>
                            {b.departureConfirmedAt && !b.checkedOutAt && (
                              <div style={{marginTop: 4}}><DepartureFlag /></div>
                            )}
                          </td>
                          <td style={{...td, fontWeight: 700, color: INK}}>{b.amount ? `$${b.amount}` : b.unitPrice ? `$${Number(b.unitPrice).toFixed(0)}/mo` : '—'}</td>
                          <td style={{...td, minWidth: 130, whiteSpace: 'nowrap'}}>
                            {b.amount != null ? (
                              <span style={{fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', borderRadius: 999, padding: '4px 10px', ...(b.paid ? {color: '#1f9d5b', background: 'rgba(3,149,64,0.12)'} : {color: '#b8770a', background: 'rgba(255,168,0,0.15)'})}}>
                                {b.paid ? 'Paid' : 'Unpaid'}
                              </span>
                            ) : (
                              <RentBadge b={b} />
                            )}
                          </td>
                          <td style={td}><StatusPill status={b.status} /></td>
                          <td style={{...td, textAlign: 'right', whiteSpace: 'nowrap'}}>
                            <button onClick={() => openTimeline(b)} disabled={rowBusy || timelineBusyId === b.id} style={{...btnGhost, minWidth: 74}}>{timelineBusyId === b.id ? <Spinner size={13} /> : 'Timeline'}</button>
                            {decidable ? (
                              <>
                                {b.status === 'PENDING' && <button onClick={() => act(b, 'waitlist')} disabled={rowBusy} style={{...btnGhost, minWidth: 78}}>{spin('waitlist', 'Waitlist')}</button>}
                                <button onClick={() => act(b, 'reject')} disabled={rowBusy} style={{...btnDanger, minWidth: 70}}>{spin('reject', 'Reject')}</button>
                                <button onClick={() => act(b, 'approve')} disabled={rowBusy} style={{...btnPrimary, minWidth: 78}}>{spin('approve', 'Approve')}</button>
                              </>
                            ) : b.status === 'APPROVED' ? (
                              <>
                                {b.checkedInAt && !b.checkedOutAt ? (
                                  <button onClick={() => act(b, 'checkout')} disabled={rowBusy} style={{...btnPrimary, minWidth: 90}}>{spin('checkout', 'Check out')}</button>
                                ) : !b.checkedInAt && isRentCovered(b) ? (
                                  <button onClick={() => act(b, 'checkin')} disabled={rowBusy} style={{...btnPrimary, minWidth: 84}}>{spin('checkin', 'Check in')}</button>
                                ) : null}
                                {!b.checkedOutAt && (!isRentCovered(b) || b.amount == null) && (
                                  <button onClick={() => setPayFor(b)} disabled={rowBusy} style={{...btnGhost, minWidth: 96}}>Record payment</button>
                                )}
                                <button onClick={() => act(b, 'cancel')} disabled={rowBusy} style={{...btnDanger, minWidth: 70}}>{spin('cancel', 'Cancel')}</button>
                              </>
                            ) : null}
                            {b.status === 'APPROVED' && Number(b.depositHeld) > 0 && <button onClick={() => resendWelcome(b)} disabled={rowBusy} style={{...btnGhost, minWidth: 128}}>{spin('resend', 'Resend welcome')}</button>}
                            {Number(b.depositHeld) > 0 && <button onClick={() => refundDeposit(b)} disabled={rowBusy} style={{...btnGhost, minWidth: 116}}>{spin('refund', 'Refund deposit')}</button>}
                            {!decidable && b.status !== 'APPROVED' && Number(b.depositHeld) === 0 && <span style={{color: MUTED, fontSize: 13}}>—</span>}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
              )}
              <div style={{borderTop: '1px solid #f1f3f7'}}>
                <Pagination page={page} total={filtered.length} pageSize={PAGE_SIZE} onChange={setPage} />
              </div>
            </>
          )}
        </div>
      </div>

      {payFor && (
        <ModalPortal><RecordPaymentModal
          booking={payFor}
          onClose={() => setPayFor(null)}
          onPaid={async () => {
            const b = payFor
            setPayFor(null)
            toast.success(`Payment recorded — receipt emailed.`)
            // Refetch so cumulative rent-paid, "paid" periods, and the app-managed
            // schedule (paid-through / next charge) all reflect the new payment.
            try {
              replaceOrDrop(await api.getBooking(b.id), b)
            } catch {
              /* keep the row as-is if the refetch fails */
            }
          }}
        /></ModalPortal>
      )}

      {timelineFor && (
        <ModalPortal>
          <BookingTimelineModal booking={timelineFor} onClose={() => setTimelineFor(null)} admin />
        </ModalPortal>
      )}

      {refundFor && (
        <ModalPortal><DepositRefundModal
          booking={refundFor.booking}
          deposit={refundFor.deposit}
          onClose={() => setRefundFor(null)}
          onRefunded={async () => {
            const b = refundFor.booking
            setRefundFor(null)
            try {
              replaceOrDrop(await api.getBooking(b.id), b)
            } catch { /* list will refresh on next load */ }
            toast.success('Deposit refunded.')
          }}
        /></ModalPortal>
      )}
    </div>
  )
}

// ─── Record payment for a booking ───────────────────────────────────────
const METHODS: PaymentMethodValue[] = ['ZELLE', 'CASH', 'CARD', 'OTHER']

/** Add n months to a YYYY-MM-DD date, keeping the move-in day → 'YYYY-MM-DD'. */
const addMonthsKeepDay = (dateStr: string, n: number): string => {
  const base = new Date(`${dateStr || '2026-01-01'}T00:00:00Z`)
  const day = base.getUTCDate()
  const d = new Date(Date.UTC(base.getUTCFullYear(), base.getUTCMonth() + n, 1))
  const lastDay = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate()
  d.setUTCDate(Math.min(day, lastDay))
  return d.toISOString().slice(0, 10)
}
/** "Jul 22" from a YYYY-MM-DD. */
const dayMonth = (iso: string) => new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', {month: 'short', day: 'numeric', timeZone: 'UTC'})
/** A billing cycle anchored to the move-in day → "Jul 22 – Aug 22, 2026". */
const cycleLabel = (start: string, end: string) => `${dayMonth(start)} – ${dayMonth(end)}, ${start.slice(0, 4)}`
const shortDate = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString() : null)

const RecordPaymentModal: FC<{booking: Booking; onClose: () => void; onPaid: (amount: string) => void}> = ({booking, onClose, onPaid}) => {
  // Default the amount to what's owed. The refundable deposit is held separately
  // and does NOT reduce rent. Fixed-term → remaining balance; open-ended monthly
  // → this month's rent (the monthly rate).
  const total = Number(booking.amount ?? 0)
  const paidSoFar = Number(booking.paidAmount ?? 0) // rent paid (excludes deposit)
  const depositHeld = Number(booking.depositHeld ?? 0)
  const monthly = Number(booking.unitPrice ?? 0)
  const isMonthly = total === 0 && monthly > 0
  const remaining = total > 0 ? Math.max(0, total - paidSoFar) : monthly
  // Billing cycles anchored to the move-in day (e.g. Jul 22 → Aug 22). Cycles
  // already covered by recorded rent are disabled; pre-move-in never appears.
  const monthsPaid = monthly > 0 ? Math.floor(paidSoFar / monthly) : 0
  const monthOptions = Array.from({length: 24}, (_, i) => {
    const start = addMonthsKeepDay(booking.startDate, i)
    const end = addMonthsKeepDay(booking.startDate, i + 1)
    return {key: start, label: cycleLabel(start, end), paid: i < monthsPaid}
  })
  const [period, setPeriod] = useState(monthOptions[Math.min(monthsPaid, 23)].key)
  const [amount, setAmount] = useState(remaining > 0 ? remaining.toFixed(2) : '')
  const [method, setMethod] = useState<PaymentMethodValue>('ZELLE')
  const [reference, setReference] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const save = async () => {
    setErr(null)
    if (!/^\d+(\.\d{1,2})?$/.test(amount)) return setErr('Enter a valid amount (e.g. 160.00).')
    if (isMonthly && monthOptions.find((o) => o.key === period)?.paid) return setErr('That period is already paid.')
    if (!booking.applicant?.id || !booking.bed?.id) return setErr('Booking is missing renter or bed.')
    setBusy(true)
    try {
      await api.recordPayment({
        userId: booking.applicant.id,
        bedId: booking.bed.id,
        bookingId: booking.id,
        amount,
        method,
        status: 'PAID',
        periodLabel: isMonthly ? `Rent · ${monthOptions.find((o) => o.key === period)?.label ?? period}` : undefined,
        reference: reference.trim() || undefined,
      })
      onPaid(amount)
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not record payment')
      setBusy(false)
    }
  }

  return (
    <div onClick={onClose} style={{position: 'fixed', inset: 0, background: 'rgba(8,10,14,0.5)', zIndex: 1100, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '50px 20px', overflowY: 'auto'}}>
      <div onClick={(e) => e.stopPropagation()} style={{background: '#fff', borderRadius: 16, padding: '26px 24px', width: '100%', maxWidth: 420, boxShadow: '0 20px 60px rgba(0,0,0,0.3)'}}>
        <h2 style={{fontSize: 20, fontWeight: 800, color: INK, margin: '0 0 4px'}}>Record payment</h2>
        <p style={{color: MUTED, fontSize: 14, margin: '0 0 18px'}}>{booking.applicant?.firstName} {booking.applicant?.lastName} · Bed {booking.bed?.code} · {booking.months != null ? `${booking.months} months` : 'monthly'}</p>
        {err && <div style={{background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', borderRadius: 8, padding: '10px 14px', fontSize: 14, marginBottom: 14}}>{err}</div>}

        {total > 0 && (
          <div style={{background: '#f0faf4', border: '1px solid #bbe6cd', borderRadius: 10, padding: '12px 14px', fontSize: 13, marginBottom: 16}}>
            <Row label='Rent total' value={`$${total.toFixed(2)}`} />
            {paidSoFar > 0 && <Row label='Rent paid' value={`− $${paidSoFar.toFixed(2)}`} />}
            <div style={{borderTop: '1px dashed #bbe6cd', margin: '6px 0'}} />
            <Row label='Balance due' value={`$${remaining.toFixed(2)}`} strong />
            {depositHeld > 0 && <Row label='Refundable deposit (held)' value={`$${depositHeld.toFixed(2)}`} />}
          </div>
        )}

        {isMonthly && (
          <div style={{background: '#f0faf4', border: '1px solid #bbe6cd', borderRadius: 10, padding: '12px 14px', fontSize: 13, marginBottom: 16}}>
            <Row label='Monthly rent' value={`$${monthly.toFixed(2)}`} strong />
            {depositHeld > 0 && <Row label='Refundable deposit (held)' value={`$${depositHeld.toFixed(2)}`} />}
            {shortDate(booking.checkedInAt) && <Row label='Checked in' value={shortDate(booking.checkedInAt)!} />}
            {shortDate(booking.checkedOutAt) && <Row label='Checked out' value={shortDate(booking.checkedOutAt)!} />}
          </div>
        )}

        {isMonthly && (
          <Field label='For which month'>
            <select value={period} onChange={(e) => setPeriod(e.target.value)} style={inp}>
              {monthOptions.map((o) => (
                <option key={o.key} value={o.key} disabled={o.paid}>{o.label}{o.paid ? ' — paid' : ''}</option>
              ))}
            </select>
          </Field>
        )}
        <Field label='Amount ($)'>
          <input value={amount} onChange={(e) => setAmount(e.target.value)} placeholder='160.00' style={inp} />
        </Field>
        <Field label='Method'>
          <select value={method} onChange={(e) => setMethod(e.target.value as PaymentMethodValue)} style={inp}>
            {METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </Field>
        <Field label='Reference (optional)'>
          <input value={reference} onChange={(e) => setReference(e.target.value)} placeholder='Zelle confirmation / memo' style={inp} />
        </Field>

        <div style={{display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16}}>
          <button onClick={onClose} style={btnGhost} disabled={busy}>Cancel</button>
          <button onClick={save} style={{...btnPrimary, minWidth: 120}} disabled={busy}>{busy ? <Spinner size={14} /> : 'Record & receipt'}</button>
        </div>
      </div>
    </div>
  )
}

// ─── Refund the held deposit with itemized deductions ───────────────────
const DepositRefundModal: FC<{booking: Booking; deposit: BookingPayment; onClose: () => void; onRefunded: () => void}> = ({booking, deposit, onClose, onRefunded}) => {
  const depositAmount = Number(deposit.amount)
  const [deductions, setDeductions] = useState<{amount: string; reason: string}[]>([])
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const setRow = (i: number, patch: Partial<{amount: string; reason: string}>) =>
    setDeductions((d) => d.map((row, idx) => (idx === i ? {...row, ...patch} : row)))

  const totalDeduct = deductions.reduce((s, d) => s + (Number(d.amount) || 0), 0)
  const net = Math.max(0, depositAmount - totalDeduct)

  const submit = async () => {
    setErr(null)
    for (const d of deductions) {
      if (!/^\d+(\.\d{1,2})?$/.test(d.amount) || Number(d.amount) <= 0) return setErr('Each deduction needs a valid amount.')
      if (!d.reason.trim()) return setErr('Each deduction needs a reason.')
    }
    if (totalDeduct > depositAmount) return setErr('Deductions exceed the deposit amount.')

    const note = deductions.length
      ? `Refunded $${net.toFixed(2)} of $${depositAmount.toFixed(2)}. Withheld: ${deductions.map((d) => `$${Number(d.amount).toFixed(2)} — ${d.reason.trim()}`).join('; ')}.`
      : `Refunded $${depositAmount.toFixed(2)} in full.`

    setBusy(true)
    try {
      await api.updatePayment(deposit.id, {status: 'REFUNDED', note})
      onRefunded()
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not refund the deposit')
      setBusy(false)
    }
  }

  return (
    <div onClick={onClose} style={{position: 'fixed', inset: 0, background: 'rgba(8,10,14,0.5)', zIndex: 1100, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '50px 20px', overflowY: 'auto'}}>
      <div onClick={(e) => e.stopPropagation()} style={{background: '#fff', borderRadius: 16, padding: '26px 24px', width: '100%', maxWidth: 460, boxShadow: '0 20px 60px rgba(0,0,0,0.3)'}}>
        <h2 style={{fontSize: 20, fontWeight: 800, color: INK, margin: '0 0 4px'}}>Refund deposit</h2>
        <p style={{color: MUTED, fontSize: 14, margin: '0 0 18px'}}>{booking.applicant?.firstName} {booking.applicant?.lastName} · Bed {booking.bed?.code} · deposit ${depositAmount.toFixed(2)}</p>
        {err && <div style={{background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', borderRadius: 8, padding: '10px 14px', fontSize: 14, marginBottom: 14}}>{err}</div>}

        <div style={{background: '#f0faf4', border: '1px solid #bbe6cd', borderRadius: 10, padding: '12px 14px', fontSize: 13, marginBottom: 16}}>
          <Row label='Deposit held' value={`$${depositAmount.toFixed(2)}`} />
          {totalDeduct > 0 && <Row label='Deductions' value={`− $${totalDeduct.toFixed(2)}`} />}
          <div style={{borderTop: '1px dashed #bbe6cd', margin: '6px 0'}} />
          <Row label='Refund to renter' value={`$${net.toFixed(2)}`} strong />
        </div>

        <Field label='Deductions (optional)'>
          {deductions.map((d, i) => (
            <div key={i} style={{display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center'}}>
              <input value={d.amount} onChange={(e) => setRow(i, {amount: e.target.value})} placeholder='5.00' style={{...inp, maxWidth: 88}} />
              <input value={d.reason} onChange={(e) => setRow(i, {reason: e.target.value})} placeholder='Reason (e.g. last bed sheet wash)' style={inp} />
              <button onClick={() => setDeductions((rows) => rows.filter((_, idx) => idx !== i))} aria-label='Remove deduction' style={{flexShrink: 0, width: 30, height: 30, borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', color: '#d9214e', cursor: 'pointer', fontSize: 16, lineHeight: 1}}>×</button>
            </div>
          ))}
          <button onClick={() => setDeductions((d) => [...d, {amount: '', reason: ''}])} style={{...btnGhost, marginLeft: 0, color: ACCENT}}>+ Add deduction</button>
        </Field>

        <p style={{fontSize: 12.5, color: MUTED, lineHeight: 1.5, margin: '4px 0 16px'}}>
          Confirm the renter's personal key fob is back on its hook. The breakdown is saved on the deposit record and shown in the timeline.
        </p>

        <div style={{display: 'flex', justifyContent: 'flex-end', gap: 10}}>
          <button onClick={onClose} disabled={busy} style={{...btnGhost, marginLeft: 0, padding: '9px 18px'}}>Cancel</button>
          <button onClick={submit} disabled={busy} style={{...btnPrimary, marginLeft: 0, padding: '9px 18px', minWidth: 130}}>{busy ? <Spinner size={14} /> : `Refund $${net.toFixed(2)}`}</button>
        </div>
      </div>
    </div>
  )
}

const Field: FC<{label: string; children: React.ReactNode}> = ({label, children}) => (
  <div style={{marginBottom: 14}}>
    <label style={{display: 'block', fontSize: 12, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6}}>{label}</label>
    {children}
  </div>
)

const Row: FC<{label: string; value: string; strong?: boolean}> = ({label, value, strong}) => (
  <div style={{display: 'flex', justifyContent: 'space-between', padding: '2px 0'}}>
    <span style={{color: MUTED}}>{label}</span>
    <span style={{fontWeight: strong ? 800 : 600, color: strong ? INK : '#334155'}}>{value}</span>
  </div>
)

const th: React.CSSProperties = {padding: '14px 18px', fontWeight: 600}
const td: React.CSSProperties = {padding: '14px 18px', verticalAlign: 'middle'}
const btnBase: React.CSSProperties = {fontSize: 13, fontWeight: 600, borderRadius: 8, padding: '7px 14px', cursor: 'pointer', marginLeft: 8, border: '1px solid transparent'}
const btnGhost: React.CSSProperties = {...btnBase, background: '#fff', border: '1px solid #e5e7eb', color: INK}
const btnPrimary: React.CSSProperties = {...btnBase, background: ACCENT, color: '#fff'}
const btnDanger: React.CSSProperties = {...btnBase, background: 'rgba(217,33,78,0.1)', color: '#d9214e'}
const inp: React.CSSProperties = {width: '100%', padding: '9px 11px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, color: INK, background: '#fff', boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit'}

export {BookingsPage}
